// @flow
import { useMutation } from '@apollo/react-hooks';
import Button from '@atlaskit/button';
import { Field } from '@atlaskit/form';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';
import React, { Fragment, useState } from 'react';
import { CREATE_WIDGET } from '../../graphql/mutations';
import { WIDGETS_QUERY } from '../../graphql/queries';
import { useFlagContext } from '../../hooks/useFlagsContext';

type Props = {
    setIsOpen: (isOpen: boolean) => void,
};

function CreateWidgetModal({ setIsOpen, onCreated }: Props) {
    // form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);

    const { pushFlag } = useFlagContext();

    const [createWidget] = useMutation(CREATE_WIDGET, {
        update(cache, { data: { createWidget: createWidgetData } }) {
            const { widgets } = cache.readQuery({ query: WIDGETS_QUERY });
            const data = {
                widgets: [...widgets, createWidgetData],
            };
            cache.writeQuery({
                query: WIDGETS_QUERY,
                data,
            });
        },
        onError(error) {
            setLoading(false);
            pushFlag('Error', error.message.replace('GraphQL error: ', ''));
        },
        onCompleted(data) {
            setLoading(false);
            setIsOpen(false);
            onCreated(data.createWidget.id);
            pushFlag('Success', 'Widget created with success!', 'success');
        },
    });

    const _submitForm = async (e) => {
        e.preventDefault();
        setLoading(true);
        const payload = { name, description };
        await createWidget({
            variables: { payload },
        });
    };

    return (
        <ModalTransition>
            <Modal shouldScrollInViewport onClose={() => setIsOpen(false)}>
                <form onSubmit={_submitForm}>
                    <ModalHeader>
                        <ModalTitle>Create new widget</ModalTitle>
                    </ModalHeader>

                    <ModalBody>
                        <Field name="name" isRequired label="Name">
                            {({ fieldProps }) => (
                                <Fragment>
                                    <TextField
                                        {...fieldProps}
                                        autoFocus={true}
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </Fragment>
                            )}
                        </Field>
                        <Field name="description" label="Description">
                            {({ fieldProps }) => (
                                <Fragment>
                                    <TextArea
                                        {...fieldProps}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    />
                                </Fragment>
                            )}
                        </Field>
                    </ModalBody>
                    <ModalFooter>
                        <Button isDisabled={loading} onClick={() => setIsOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            isDisabled={loading}
                            appearance="primary"
                            iconAfter={loading ? <Spinner size="small" /> : null}
                        >
                            Create widget
                        </Button>
                    </ModalFooter>
                </form>
            </Modal>
        </ModalTransition>
    );
}

export default CreateWidgetModal;
